import { observer } from 'mobx-react';
import { toCalendarItem } from 'owa-calendar-event-converter';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { getIsDarkTheme } from 'owa-fabric-theme';
import React from 'react';
import {
    MessageBody,
    isConfettiEnabled,
    getConfettiHandler,
    CONFETTI_HANDLER_NAME,
    INLINE_IMAGE_HANDLER_NAME,
    InlineImageHandler,
} from 'owa-controls-content-handler';
import type { ContentHandler } from 'owa-controls-content-handler-base';
import { DarkModeHandler, DARK_MODE_HANDLER_NAME } from 'owa-controls-content-handler-dark-mode';
import { getPaletteAsRawColors } from 'owa-theme';
import { FluidOwaSource } from 'owa-fluid-validations';
import { logTelemetryForJoinMeetingLink } from 'owa-online-meeting/lib/utils/logTelemetryForJoinMeetingLink';
import storeLinkHandler, {
    STORE_LINK_HANDLER_NAME,
} from 'owa-addins-marketplace-routing/lib/storeLinkHandler';
import type { ViewType } from 'owa-calendar-actions';

// The context used for inline image load (load from existing content, draft, forward etc.)
const CONTEXT_CALENDAR = 'Calendar';

export interface CalendarEventBodyProps {
    /** The calendar event */
    calendarEvent: CalendarEvent;
    viewType: ViewType;
}

export interface CalendarEventBodyState {
    /** The content handler keys */
    contentHandlerKeys: string[];
}

import styles from './CalendarEventBody.scss';

@observer
export default class CalendarEventBody extends React.Component<
    CalendarEventBodyProps,
    CalendarEventBodyState
> {
    private messageBodyWrapperRef: HTMLElement;

    private readonly contentHandlerDictionary = {};

    constructor(props: CalendarEventBodyProps) {
        super(props);

        this.initContentHandlers();
    }

    public addContentHandler(key: string, handler: ContentHandler) {
        this.contentHandlerDictionary[key] = handler;
        const contentHandlerKeys = this.state.contentHandlerKeys;
        contentHandlerKeys.push(key);
        this.setState({ contentHandlerKeys: [...contentHandlerKeys] });
    }

    public callJoinMeetingsTelemetryLogger = (onclickEvent: any) => {
        logTelemetryForJoinMeetingLink(
            this.props.viewType,
            onclickEvent,
            this.props.calendarEvent,
            false /** isCTRLKeyAndClickEnforced */
        );
    };

    public componentDidMount() {
        if (this.messageBodyWrapperRef) {
            this.messageBodyWrapperRef.addEventListener(
                'click',
                this.callJoinMeetingsTelemetryLogger
            );
        }
    }

    public componentWillUnmount() {
        if (this.messageBodyWrapperRef) {
            this.messageBodyWrapperRef.removeEventListener(
                'click',
                this.callJoinMeetingsTelemetryLogger
            );
        }
    }

    private initContentHandlers() {
        // If the item has blocked images do not create the inline image handler
        if (!this.props.calendarEvent.HasBlockedImages) {
            this.contentHandlerDictionary[INLINE_IMAGE_HANDLER_NAME] = new InlineImageHandler(
                CONTEXT_CALENDAR,
                false /* usePlaceHolder */,
                this.props.calendarEvent.ParentFolderId.mailboxInfo.userIdentity
            );
        }

        this.contentHandlerDictionary[DARK_MODE_HANDLER_NAME] = new DarkModeHandler(
            () => getPaletteAsRawColors().white
        );

        this.state = {
            contentHandlerKeys: [INLINE_IMAGE_HANDLER_NAME],
        };

        this.addContentHandler(STORE_LINK_HANDLER_NAME, storeLinkHandler);

        if (isConfettiEnabled() && this.props.calendarEvent) {
            this.contentHandlerDictionary[CONFETTI_HANDLER_NAME] = getConfettiHandler(
                this.props.calendarEvent.Body,
                false /* fireOnCreate */
            );
            this.state.contentHandlerKeys.push(CONFETTI_HANDLER_NAME);
        }
    }

    render() {
        const { calendarEvent } = this.props;

        if (calendarEvent.Body?.Value) {
            // Render HTML content
            return (
                <div ref={ref => (this.messageBodyWrapperRef = ref)}>
                    <MessageBody
                        className={styles.messageBody}
                        messageBody={calendarEvent.Body?.Value}
                        copyAllowed={true}
                        printAllowed={true}
                        isLoading={false}
                        item={toCalendarItem(calendarEvent)}
                        externalContentHandlerDictionary={this.contentHandlerDictionary}
                        externalContentHandlerKeys={[
                            ...this.state.contentHandlerKeys,
                            ...(getIsDarkTheme() ? [DARK_MODE_HANDLER_NAME] : []),
                        ]}
                        externalMouseUpHandlerList={[]}
                        fluidSource={FluidOwaSource.AgendaEditor}
                    />
                </div>
            );
        } else if (calendarEvent.Preview) {
            // Render plain text
            return calendarEvent.Preview;
        }

        return null;
    }
}
